import * as React from 'react';

import { Box, Typography } from '@mui/material';

import AdvancedGrid from './AdvancedGrid';
import {
  DEMO_DIMENSIONS,
  DIMENSION_LABEL,
  inLevelOrder,
  type DemoDimension,
  type Demographic,
  type DimensionDraft,
} from './demographic';
import DistributionBar from './DistributionBar';

/**
 * The audience a saved translation was rendered against, read-only.
 *
 * Shown rather than summarised, because checking what a translation covers is
 * the main reason to open one — but not editable, since every number it
 * produced was rendered against this grid. Changing it would leave the output
 * describing a population the translation was never run for.
 *
 * Takes the loose jsonb shape rather than `Demographic`: a row may be missing a
 * dimension, or carry the string `"typical"`, which `build_demo_grid` still
 * accepts even though the picker no longer writes it.
 */

type Props = { demographic: Record<string, unknown> | null };

/** Stored as shares summing to 1; read as the percentages a person understands. */
export const storedWeights = (
  demographic: Record<string, unknown> | null,
  dimension: DemoDimension,
): DimensionDraft | null => {
  const v = demographic?.[dimension];
  if (!v || typeof v !== 'object') return null;
  return Object.fromEntries(
    Object.entries(v as Record<string, number>).map(([level, share]) => [
      level,
      Math.round(share * 100),
    ]),
  );
};

const DimensionRow: React.FC<{
  dimension: DemoDimension;
  demographic: Record<string, unknown> | null;
}> = ({ dimension, demographic }) => {
  const weights = storedWeights(demographic, dimension);
  const label = DIMENSION_LABEL[dimension];

  if (!weights) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {demographic?.[dimension] === 'typical'
            ? 'Typical — the mean of this factor’s coefficients, one synthetic cell.'
            : 'Not recorded.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <DistributionBar
        readOnly
        label={label}
        levels={inLevelOrder(dimension, Object.keys(weights))}
        weights={weights}
        onChange={() => undefined}
      />
    </Box>
  );
};

const AudienceFrame: React.FC<Props> = ({ demographic }) => (
  <>
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Audience
      </Typography>
      {DEMO_DIMENSIONS.map((d) => (
        <DimensionRow key={d} dimension={d} demographic={demographic} />
      ))}
    </Box>

    <AdvancedGrid demographic={demographic as unknown as Demographic} />
  </>
);

export default AudienceFrame;
